import React, { forwardRef, type ReactNode, type Ref } from 'react';
import { CustomThemeButton } from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import { SpotlightPulse, SpotlightTarget } from '@atlaskit/onboarding';
import Tooltip from '@atlaskit/tooltip';
import { IssueActionsSpotlight } from '@atlassian/jira-command-palette-discoverability/src/ui/issue-actions-spotlight/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import messages from './messages.tsx';
import { pulseButtonTheme } from './styled.tsx';

type Props = {
	isOpen: boolean;
	isDisabled?: boolean;
	isLoading?: boolean;
	hasPulse: boolean;
	onClick?: () => void;
	ariaExpanded?: boolean;
};

type WrapperProps = {
	shouldWrap: boolean;
	children: ReactNode;
};

export const Wrapper = ({ children, shouldWrap }: WrapperProps) => {
	if (fg('enable_redaction_support_in_jira_issue_view')) {
		return (
			<SpotlightTarget name="issue-action-menu-button">
				{shouldWrap ? <IssueActionsSpotlight>{children}</IssueActionsSpotlight> : children}
			</SpotlightTarget>
		);
	}
	return shouldWrap ? <IssueActionsSpotlight>{children}</IssueActionsSpotlight> : children;
};

const DropdownTrigger = forwardRef(
	({ isOpen, isDisabled, isLoading, hasPulse, onClick }: Props, ref?: Ref<HTMLElement>) => {
		const { formatMessage } = useIntl();
		const intlTooltipText = formatMessage(messages.actions);
		const buttonTheme = hasPulse ? pulseButtonTheme : undefined;

		return (
			<Wrapper shouldWrap={!isLoading && !isDisabled}>
				<UFOSegment name="issue-meatball-menu-button">
					{isVisualRefreshEnabled() ? (
						<SpotlightPulse radius={3} pulse={hasPulse}>
							<Tooltip content={intlTooltipText} position="top">
								<IconButton
									testId="issue-meatball-menu.ui.dropdown-trigger.button"
									icon={() => <MoreIcon label="" LEGACY_size="medium" />}
									isSelected={isOpen}
									isDisabled={isDisabled}
									isLoading={isLoading}
									spacing="default"
									onClick={onClick}
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									ref={ref as Ref<HTMLButtonElement>}
									interactionName="auto-generated.issue-meatball-menu.ui.dropdown-trigger.button.button"
									label={intlTooltipText}
									aria-haspopup="true"
								/>
							</Tooltip>
						</SpotlightPulse>
					) : (
						<Tooltip content={intlTooltipText} position="top">
							<CustomThemeButton
								testId="issue-meatball-menu.ui.dropdown-trigger.button"
								aria-label={intlTooltipText}
								aria-expanded={isOpen}
								aria-haspopup="true"
								isDisabled={isDisabled}
								isLoading={isLoading}
								appearance="subtle"
								spacing="default"
								iconBefore={<MoreIcon label="" LEGACY_size="medium" spacing="spacious" />}
								theme={buttonTheme}
								isSelected={isOpen}
								onClick={onClick}
								interactionName="auto-generated.issue-meatball-menu.ui.dropdown-trigger.button.button"
								ref={ref}
							/>
						</Tooltip>
					)}
				</UFOSegment>
			</Wrapper>
		);
	},
);

DropdownTrigger.defaultProps = {
	hasPulse: false,
	isDisabled: false,
};

export default DropdownTrigger;
